import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid2,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Formik, Form, FieldArray, FormikErrors, FormikTouched } from 'formik';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import useCurrency from '../../../../components/CurrencyProvider';
import {
  autocompleteSx,
  Clinic,
  filterInputSubLabelSx,
  mapUserClinicsResponse,
  UserClinicDepartments,
} from '../../../Inventory/Common/inventory.helper';
import { useClinic } from '../../../../components/ClinicProvider';
import { useAlert } from '../../../../components/alertProvider';
import { STATUS_OPTIONS } from '../ApprovalDummyData';
import { useDocumentTypes, useApprovalRuleDetail } from '../inventoryApproval.queries';
import {
  ApprovalLevelFormValues,
  CREATE_APPROVAL_RULE_INITIAL_VALUES,
} from './createApprovalRule.types';
import {
  createApprovalRuleValidationSchema,
  getEmptyApprovalLevel,
  mapApprovalRuleToFormValues,
  mapFormValuesToPayload,
} from './createApprovalRule.helper';
import { useRoles, useCreateApprovalRule, useUpdateApprovalRule } from './createApprovalRule.queries';
import { ApprovalRuleApiError } from './createApprovalRule.types';
import {
  createRuleContainerSx,
  createRuleTitleSx,
  sectionTitleSx,
  sectionBoxSx,
  formFieldSx,
  workflowCardSx,
  removeWorkflowBtnSx,
  addLevelBtnSx,
  mandatoryToggleSx,
  footerContainerSx,
  cancelBtnSx,
  previewBtnSx,
  saveRuleBtnSx,
  checkBoxSx,
  notifyLabelCheckedSx,
  notifyLabelUncheckedSx,
  toggleActiveLabelSx,
  toggleLabelSx,
  addLevelInfoBoxSx,
} from './CreateApprovalRuleSx';

import PreviewWorkflowModal from './PreviewWorkflowModal';
import { ConfirmationModal } from '../../../../components/confirmationModal';
import DiscardConfirmationModal from '../../../../components/confirmationModal/DiscardConfirmationModal';

import { useTranslation } from 'react-i18next';
import styles from '../InventoryApproval.module.css';


const CreateApprovalRule: React.FC = () => {
  const navigate = useNavigate();
  const { ruleId } = useParams<{ ruleId: string }>();
  const isEditMode = Boolean(ruleId);
  const { ClinicId } = useClinic();
  const { currencySymbol } = useCurrency();
  const { addAlert } = useAlert();
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');
  const { data: rolesData, isLoading: rolesLoading, isError: rolesError } = useRoles();
  const { data: documentTypeOptions = [], isLoading: docTypesLoading, isError: docTypesError } = useDocumentTypes();
  const { mutate: submitApprovalRule, isPending: isSubmitting } = useCreateApprovalRule();
  const { mutate: updateApprovalRuleMutate, isPending: isUpdating } = useUpdateApprovalRule();
  const { data: editRule, isLoading: ruleLoading } = useApprovalRuleDetail(
    isEditMode ? Number(ruleId) : undefined
  );
  const roleOptions = rolesData?.objects.map((r) => ({
    label: r.name_label,
    value: String(r.id),
  })) ?? [];
  const [clinicList, setClinicList] = useState<Clinic[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [removeLevelConfirm, setRemoveLevelConfirm] = useState<{ open: boolean; idx: number }>({ open: false, idx: -1 });
  const [openDiscardModal, setOpenDiscardModal] = useState(false);
  const [clinicChangeConfirmOpen, setClinicChangeConfirmOpen] = useState(false);
  const [pendingClinic, setPendingClinic] = useState<Clinic | null | undefined>(undefined);
  const pendingSubmitRef = useRef<(() => void) | null>(null);

  const extractApiErrorMessage = (error: unknown, fallback: string): string => {
    if (
      error !== null &&
      typeof error === 'object' &&
      'response' in error &&
      (error as { response?: { data?: unknown } }).response?.data !== undefined
    ) {
      const data = (error as { response: { data: unknown } }).response.data as ApprovalRuleApiError;
      if (data?.errors?.length > 0 && data.errors[0].detail) {
        return data.errors[0].detail;
      }
    }
    if (error instanceof Error) return error.message;
    return fallback;
  };

  const handleCancelClick = (dirty: boolean) => {
    if (dirty) {
      setOpenDiscardModal(true);
    } else {
      navigate('/ai/configuration/inventory-approval');
    }
  };

  const handleConfirmDiscard = () => {
    setOpenDiscardModal(false);
    navigate('/ai/configuration/inventory-approval');
  };

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr) as UserClinicDepartments;
      const mapped = mapUserClinicsResponse(user);
      setClinicList(mapped);
    } catch {
      setClinicList([]);
    }
  }, [ClinicId]);

  const initialValues = editRule
    ? mapApprovalRuleToFormValues(editRule)
    : CREATE_APPROVAL_RULE_INITIAL_VALUES;

  if (isEditMode && ruleLoading) {
    return (
      <Box sx={createRuleContainerSx} display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={createRuleContainerSx}>
      <Typography sx={createRuleTitleSx}>
        {isEditMode ? tInventoryApproval('edit_approval_rule') : tInventoryApproval('create_new_approval_rule')}
      </Typography>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validateOnMount
        validationSchema={createApprovalRuleValidationSchema(tInventoryApproval)}
        onSubmit={(values) => {
          const payload = mapFormValuesToPayload(values);
          // Store the actual API call and open confirmation
          pendingSubmitRef.current = () => {
            if (isEditMode && ruleId) {
              updateApprovalRuleMutate({ id: Number(ruleId), payload }, {
                onSuccess: () => {
                  addAlert(tInventoryApproval('rule_updated_success'), 'success');
                  navigate('/ai/configuration/inventory-approval');
                },
                onError: (error: unknown) => {
                  addAlert(extractApiErrorMessage(error, tInventoryApproval('something_went_wrong')), 'error');
                },
              });
            } else {
              submitApprovalRule(payload, {
                onSuccess: () => {
                  addAlert(tInventoryApproval('rule_created_success'), 'success');
                  navigate('/ai/configuration/inventory-approval');
                },
                onError: (error: unknown) => {
                  addAlert(extractApiErrorMessage(error, tInventoryApproval('something_went_wrong')), 'error');
                },
              });
            }
          };
          setSaveConfirmOpen(true);
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched, submitForm, isValid, dirty }) => {
          const levelErrors = errors.approvalLevels as FormikErrors<ApprovalLevelFormValues>[] | undefined;
          const levelTouched = touched.approvalLevels as FormikTouched<ApprovalLevelFormValues>[] | undefined;

          return (
            <Form>
              {/* ── Basic Details ── */}
              <Box sx={sectionBoxSx}>
                <Typography sx={sectionTitleSx}>{tInventoryApproval('basic_details')}</Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      name="ruleName"
                      label={tInventoryApproval('rule_name')}
                      size="small"
                      fullWidth
                      value={values.ruleName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.ruleName && errors.ruleName)}
                      helperText={touched.ruleName && errors.ruleName}
                      sx={formFieldSx}
                      inputProps={{ 'data-testid': 'approval-rule-name-input' }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Autocomplete
                      options={documentTypeOptions}
                      value={documentTypeOptions.find((o) => o.id === values.documentType) ?? null}
                      onChange={(_, v) => setFieldValue('documentType', v?.id ?? null)}
                      onBlur={() => setFieldTouched('documentType', true)}
                      getOptionLabel={(o) => o.name}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      loading={docTypesLoading}
                      disabled={docTypesError}
                      ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                      data-testid="approval-document-type-autocomplete"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tInventoryApproval('document_type')}
                          size="small"
                          sx={formFieldSx}
                          error={Boolean((touched.documentType && errors.documentType) || docTypesError)}
                          helperText={
                            docTypesError
                              ? tInventoryApproval('doc_type_load_error')
                              : touched.documentType && errors.documentType
                          }
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {docTypesLoading ? <CircularProgress size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Autocomplete
                      options={clinicList}
                      value={clinicList.find((o) => o.id === values.clinicId) ?? null}
                      onChange={(_, v) => {
                        const hasLevelData =
                          values.approvalLevels.length > 1 ||
                          values.approvalLevels.some((lvl) => lvl.role !== null);
                        if (hasLevelData) {
                          setPendingClinic(v);
                          setClinicChangeConfirmOpen(true);
                        } else {
                          setFieldValue('clinicId', v?.id ?? null);
                          setFieldValue('approvalLevels', [getEmptyApprovalLevel(1)]);
                        }
                      }}
                      onBlur={() => setFieldTouched('clinicId', true)}
                      getOptionLabel={(o) => o.name_label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                      data-testid="approval-clinic-autocomplete"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tInventoryApproval('clinic')}
                          size="small"
                          sx={formFieldSx}
                          error={Boolean(touched.clinicId && errors.clinicId)}
                          helperText={touched.clinicId && errors.clinicId}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Autocomplete
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find((o) => o.value === values.status) ?? null}
                      onChange={(_, v) => setFieldValue('status', v?.value ?? '')}
                      onBlur={() => setFieldTouched('status', true)}
                      getOptionLabel={(o) => o.value === 'ACTIVE' ? tInventoryApproval('active') : tInventoryApproval('inactive')}
                      isOptionEqualToValue={(o, v) => o.value === v.value}
                      ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                      data-testid="approval-status-autocomplete"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tInventoryApproval('status')}
                          size="small"
                          sx={formFieldSx}
                          error={Boolean(touched.status && errors.status)}
                          helperText={touched.status && errors.status}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Box>

              {/* ── Trigger Conditions ── */}
              <Box sx={sectionBoxSx}>
                <Typography sx={sectionTitleSx}>{tInventoryApproval('trigger_conditions')}</Typography>
                <Grid2 container spacing={2} alignItems="flex-start">
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      name="minAmount"
                      label={tInventoryApproval('min_amount', { symbol: currencySymbol })}
                      type="number"
                      size="small"
                      fullWidth
                      value={values.minAmount ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.minAmount && errors.minAmount)}
                      helperText={touched.minAmount && errors.minAmount}
                      sx={formFieldSx}
                      inputProps={{ 'data-testid': 'approval-min-amount-input' }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      name="maxAmount"
                      label={tInventoryApproval('max_amount', { symbol: currencySymbol })}
                      type="number"
                      size="small"
                      fullWidth
                      disabled={values.isUnlimited}
                      value={values.isUnlimited ? '' : (values.maxAmount ?? '')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.maxAmount && errors.maxAmount && !values.isUnlimited)}
                      helperText={!values.isUnlimited && touched.maxAmount && errors.maxAmount}
                      sx={formFieldSx}
                      inputProps={{ 'data-testid': 'approval-max-amount-input' }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isUnlimited}                     
                          onChange={(e) => {
                            setFieldValue('isUnlimited', e.target.checked);
                            if (e.target.checked) setFieldValue('maxAmount', null);
                          }}
                          sx={{ ...checkBoxSx, p: 0.5, ml: 1 , mr:1}}
                          inputProps={{ 'data-testid': 'approval-unlimited-checkbox' } as React.InputHTMLAttributes<HTMLInputElement>}
                          icon={<span className={styles.AutoCompleteDropDownIcon} />}
                          checkedIcon={
                            <span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>
                          }
                        />
                      }
                      label={tInventoryApproval('unlimited')}
                      sx={{ mt: 0.5, ...(values.isUnlimited ? notifyLabelCheckedSx : notifyLabelUncheckedSx) }}
                    />
                  </Grid2>
                </Grid2>
              </Box>

              {/* ── Approval Workflow Builder ── */}
              <Box sx={sectionBoxSx}>
                <Typography sx={sectionTitleSx}>{tInventoryApproval('approval_workflow_builder')}</Typography>
                <FieldArray name="approvalLevels">
                  {({ push }) => {
                    const handleAddApprovalLevel = () => {
                      const currentMaxLevel = values.approvalLevels.reduce(
                        (maxLevel, levelItem) =>
                          typeof levelItem.level === 'number' && !Number.isNaN(levelItem.level)
                            ? Math.max(maxLevel, levelItem.level)
                            : maxLevel,
                        0
                      );
                      const nextLevel = currentMaxLevel + 1;
                      push(getEmptyApprovalLevel(nextLevel));
                    };

                    const handleRemoveApprovalLevel = (idx: number) => {
                      setRemoveLevelConfirm({ open: true, idx });
                    };

                    const confirmRemoveApprovalLevel = () => {
                      const idx = removeLevelConfirm.idx;
                      setRemoveLevelConfirm({ open: false, idx: -1 });
                      const updatedLevels = values.approvalLevels
                        .filter((_, i) => i !== idx)
                        .map((levelItem, i) => ({ ...levelItem, level: i + 1 }));
                      setFieldValue('approvalLevels', updatedLevels);
                    };

                    const selectedClinic = clinicList.find((c) => c.id === values.clinicId);
                    const levelLimit = selectedClinic?.inventory_level_limit ?? 0;
                    const isLevelFieldsDisabled = !!values.clinicId && levelLimit === 0;
                    const isAddLevelDisabled =
                      !values.clinicId ||
                      levelLimit === 0 ||
                      values.approvalLevels.length >= levelLimit;
                    const addLevelInfoText = !values.clinicId
                      ? tInventoryApproval('select_clinic_first')
                      : levelLimit === 0
                        ? tInventoryApproval('no_level_limit_configured')
                        : values.approvalLevels.length >= levelLimit
                          ? tInventoryApproval('max_levels_reached', { count: levelLimit })
                          : '';

                    return (
                      <>
                        {values.approvalLevels.map((lvl, idx) => (
                          <Box key={lvl.id ?? lvl.level} sx={workflowCardSx}>
                            <Grid2 container spacing={2} alignItems="flex-start">
                              {/* Level */}
                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                  label={tInventoryApproval('level_label')}
                                  size="small"
                                  fullWidth
                                  value={tInventoryApproval('level_value', { level: lvl.level })}
                                  disabled
                                  sx={formFieldSx}
                                />
                              </Grid2>

                              {/* Role */}
                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Autocomplete
                                  options={roleOptions}
                                  loading={rolesLoading}
                                  disabled={rolesError || isLevelFieldsDisabled}
                                  value={roleOptions.find((o) => o.value === String(lvl.role)) ?? null}
                                  onChange={(_, v) => {
                                    setFieldValue(`approvalLevels.${idx}.role`, v ? Number(v.value) : null);
                                    setFieldTouched(`approvalLevels.${idx}.role`, true, false);                               
                                  }}
                                  onBlur={() => setFieldTouched(`approvalLevels.${idx}.role`, true)}
                                  getOptionLabel={(o) => o.label}
                                  isOptionEqualToValue={(o, v) => o.value === v.value}
                                  ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                                  data-testid={`approval-level-${idx}-role-autocomplete`}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={tInventoryApproval('role')}
                                      size="small"
                                      sx={formFieldSx}
                                      error={Boolean((levelTouched?.[idx]?.role && levelErrors?.[idx] && (levelErrors[idx] as FormikErrors<ApprovalLevelFormValues>)?.role) || rolesError)}
                                      helperText={
                                        rolesError
                                          ? tInventoryApproval('roles_load_error')
                                          : levelTouched?.[idx]?.role && levelErrors?.[idx] && (levelErrors[idx] as FormikErrors<ApprovalLevelFormValues>)?.role
                                      }
                                    />
                                  )}
                                />
                              </Grid2>

                              {/* Escalation Time */}
                              <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                                <TextField
                                  name={`approvalLevels.${idx}.escalationTime`}
                                  label={tInventoryApproval('escalation_time')}
                                  type="time"
                                  size="small"
                                  fullWidth
                                  disabled={isLevelFieldsDisabled}
                                  className={styles.timePicker}
                                  slotProps={{ inputLabel: { shrink: true } }}
                                  value={lvl.escalationTime}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={Boolean(levelTouched?.[idx]?.escalationTime && levelErrors?.[idx] && (levelErrors[idx] as FormikErrors<ApprovalLevelFormValues>)?.escalationTime)}
                                  helperText={levelTouched?.[idx]?.escalationTime && levelErrors?.[idx] && (levelErrors[idx] as FormikErrors<ApprovalLevelFormValues>)?.escalationTime}
                                  sx={formFieldSx}
                                  inputProps={{ 'data-testid': `approval-level-${idx}-escalation-time-input` }}
                                />
                              </Grid2>

                              {/* Escalation Role */}
                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Autocomplete
                                  options={roleOptions}
                                  loading={rolesLoading}
                                  disabled={rolesError || isLevelFieldsDisabled}
                                  value={roleOptions.find((o) => o.value === String(lvl.escalationRole)) ?? null}
                                  onChange={(_, v) => {
                                    setFieldValue(`approvalLevels.${idx}.escalationRole`, v ? Number(v.value) : null);
                                    setFieldTouched(`approvalLevels.${idx}.escalationRole`, true, false);
                                  }}
                                  onBlur={() => setFieldTouched(`approvalLevels.${idx}.escalationRole`, true)}
                                  getOptionLabel={(o) => o.label}
                                  isOptionEqualToValue={(o, v) => o.value === v.value}
                                  ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                                  data-testid={`approval-level-${idx}-escalation-role-autocomplete`}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={tInventoryApproval('escalation_role')}
                                      size="small"
                                      sx={formFieldSx}
                                      error={Boolean((levelTouched?.[idx]?.escalationRole && levelErrors?.[idx] && (levelErrors[idx] as FormikErrors<ApprovalLevelFormValues>)?.escalationRole) || rolesError)}
                                      helperText={
                                        rolesError
                                          ? tInventoryApproval('roles_load_error')
                                          : levelTouched?.[idx]?.escalationRole && levelErrors?.[idx] && (levelErrors[idx] as FormikErrors<ApprovalLevelFormValues>)?.escalationRole
                                      }
                                    />
                                  )}
                                />
                              </Grid2>

                              {/* Remove button */}
                              <Grid2 size={{ xs: 12, md: 1 }} display="flex" justifyContent="center" alignItems="center">
                                {values.approvalLevels.length > 1 && (
                                  <Tooltip title={tInventoryApproval('remove_level_tooltip')} arrow>
                                    <IconButton
                                      sx={removeWorkflowBtnSx}
                                      data-testid={`approval-remove-level-${idx}-btn`}
                                      onClick={() => handleRemoveApprovalLevel(idx)}
                                    >
                                      <CancelOutlinedIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Grid2>
                            </Grid2>

                            {/* Toggles row */}
                            <Box display="flex" alignItems="center" gap={3} mt={1.5}>
                              <Box display="flex" alignItems="center">
                                <Switch
                                  checked={lvl.isMandatory}
                                  disabled={isLevelFieldsDisabled}
                                  onChange={(e) => setFieldValue(`approvalLevels.${idx}.isMandatory`, e.target.checked)}
                                  sx={mandatoryToggleSx}
                                  inputProps={{ 'data-testid': `approval-level-${idx}-mandatory-toggle` } as React.InputHTMLAttributes<HTMLInputElement>}
                                />
                                <Typography sx={lvl.isMandatory ? toggleLabelSx : toggleActiveLabelSx}>{tInventoryApproval('mandatory_toggle')}</Typography>
                              </Box>
                              <Box display="flex" alignItems="center">
                                <Switch
                                  checked={lvl.skipIfSameUser}
                                  disabled={isLevelFieldsDisabled}
                                  onChange={(e) => setFieldValue(`approvalLevels.${idx}.skipIfSameUser`, e.target.checked)}
                                  sx={mandatoryToggleSx}
                                  inputProps={{ 'data-testid': `approval-level-${idx}-skip-same-user-toggle` } as React.InputHTMLAttributes<HTMLInputElement>}
                                />
                                <Typography  sx={lvl.skipIfSameUser ? toggleLabelSx : toggleActiveLabelSx}>{tInventoryApproval('skip_if_same_user')}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}

                        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                          <Button
                            startIcon={<AddIcon />}
                            sx={addLevelBtnSx}
                            onClick={handleAddApprovalLevel}
                            disabled={isAddLevelDisabled}
                            data-testid="approval-add-level-btn">
                            {tInventoryApproval('add_approval_level')}
                          </Button>
                          {isAddLevelDisabled && addLevelInfoText && (
                            <Box sx={addLevelInfoBoxSx}>
                              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                              <Typography variant="caption" fontWeight={500}>
                                {addLevelInfoText}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* ── Remove Level Confirmation ── */}
                        <ConfirmationModal
                          open={removeLevelConfirm.open}
                          handleClose={() => setRemoveLevelConfirm({ open: false, idx: -1 })}
                          onConfirm={confirmRemoveApprovalLevel}
                          type="delete"
                          title={tInventoryApproval('remove_approval_level')}
                          contentMessage={tInventoryApproval('remove_level_confirm', { level: removeLevelConfirm.idx + 1 })}
                        />
                      </>
                    );
                  }}
                </FieldArray>
              </Box>

              {/* ── Notification Settings ── */}
              <Box sx={sectionBoxSx}>
                <Typography sx={sectionTitleSx}>{tInventoryApproval('notification_settings')}</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.notifyEmail}
                        onChange={(e) => setFieldValue('notifyEmail', e.target.checked)}                   
                        sx={{ ...checkBoxSx, p: 0.5, ml: 1 , mr:1}}
                        inputProps={{ 'data-testid': 'approval-notify-email-checkbox' } as React.InputHTMLAttributes<HTMLInputElement>}
                        icon={<span className={styles.AutoCompleteDropDownIcon} />}
                        checkedIcon={
                          <span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>
                        }
                      />
                    }
                    label={tInventoryApproval('notify_email')}
                    sx={values.notifyEmail ? notifyLabelCheckedSx : notifyLabelUncheckedSx}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.notifySystem}
                        onChange={(e) => setFieldValue('notifySystem', e.target.checked)}
                        sx={{ ...checkBoxSx, p: 0.5, ml: 1 , mr:1}}
                        inputProps={{ 'data-testid': 'approval-notify-system-checkbox' } as React.InputHTMLAttributes<HTMLInputElement>}
                        icon={<span className={styles.AutoCompleteDropDownIcon} />}
                        checkedIcon={
                          <span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>
                        }
                      />
                    }
                    label={tInventoryApproval('notify_system')}
                    sx={values.notifySystem ? notifyLabelCheckedSx : notifyLabelUncheckedSx}
                  />
                </Box>
              </Box>

              {/* ── Footer ── */}
              <Box sx={footerContainerSx}>
                <Button
                  variant="outlined"
                  sx={cancelBtnSx}
                  onClick={() => handleCancelClick(dirty)}
                  data-testid="approval-cancel-btn"
                >
                  {tInventoryApproval('cancel')}
                </Button>
                <Button variant="outlined" sx={previewBtnSx} type="button" disabled={!isValid} onClick={() => setPreviewOpen(true)} data-testid="approval-preview-workflow-btn">
                  {tInventoryApproval('preview_workflow')}
                </Button>
                <Button
                  variant="contained"
                  sx={saveRuleBtnSx}
                  type="submit"
                  disabled={isSubmitting || isUpdating || (isEditMode && !dirty)}
                  data-testid="approval-save-rule-btn"
                >
                  {isEditMode ? tInventoryApproval('update_rule') : tInventoryApproval('save_rule')}
                </Button>
              </Box>

              {/* ── Preview Workflow Modal ── */}
              <PreviewWorkflowModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                values={values}
                roleOptions={roleOptions}
                onSave={() => {
                  setPreviewOpen(false);
                  submitForm();
                }}
                isEditMode={isEditMode}
                onDisabled={isSubmitting || isUpdating || (isEditMode && !dirty)}
              />

              {/* ── Discard Confirmation ── */}
              <DiscardConfirmationModal
                open={openDiscardModal}
                onClose={() => setOpenDiscardModal(false)}
                onDiscard={handleConfirmDiscard}
              />

              {/* ── Clinic Change Confirmation ── */}
              <ConfirmationModal
                open={clinicChangeConfirmOpen}
                handleClose={() => {
                  setClinicChangeConfirmOpen(false);
                  setPendingClinic(undefined);
                }}
                onConfirm={() => {
                  setClinicChangeConfirmOpen(false);
                  setFieldValue('clinicId', pendingClinic?.id ?? null);
                  setFieldValue('approvalLevels', [getEmptyApprovalLevel(1)]);
                  setPendingClinic(undefined);
                }}
                type="warning"
                title={tInventoryApproval('change_clinic_title')}
                contentMessage={tInventoryApproval('change_clinic_message')}
              />

              {/* ── Save/Update Confirmation ── */}
              <ConfirmationModal
                open={saveConfirmOpen}
                handleClose={() => setSaveConfirmOpen(false)}
                onConfirm={() => {
                  setSaveConfirmOpen(false);
                  pendingSubmitRef.current?.();
                }}
                type="save"
                title={isEditMode ? tInventoryApproval('update_confirmation_title') : tInventoryApproval('save_confirmation_title')}
                contentMessage={isEditMode
                  ? tInventoryApproval('update_confirmation_message')
                  : tInventoryApproval('save_confirmation_message')}
              />
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateApprovalRule;
